import React, { useEffect, useState } from 'react'
import { useAuth } from '@hssoft/auth-web-shared-lib'
import { AdminModeBar } from '@hssoft/design-system-shared-lib'
import { useTranslation } from 'react-i18next'
import { Route, Switch } from 'react-router'
import moment from 'moment'
import 'moment/locale/de'
import 'moment/locale/en-gb'
import 'moment/locale/fr'

import Dashboard from './pages/Dashboard/'
import BranchDetails from './pages/BranchDetails/'

import initDataQuery from 'gql/query/initData'
import { DashboardContext } from 'components/DashboardContext'
import { useQuery } from 'components/graphql'
import AppHeader from 'components/AppHeader'
import { isAdmin } from 'components/withAuth'
import { getUserLanguage } from 'i18n/userLanguage'
import { Spinner } from 'media/icons'
import { LoadingWrapper, StyledAdminModeBarWrapper, Wrapper, ADMIN_MODE_BAR_HEIGHT_PX } from 'style/styled'
import { version } from '../package.json'

const formattingLocale = 'de'
moment.locale(formattingLocale)

// Enforce ISO 8601 week (Monday-first, week 1 contains Jan 4) for every
// supported locale so charts and pickers never fall back to Sunday-first.
;['de', 'en', 'en-gb', 'fr'].forEach(locale => {
	moment.updateLocale(locale, { week: { dow: 1, doy: 4 } })
})
moment.locale(formattingLocale)

const App = () => {
	const { data, loading: isLoading, error } = useQuery(initDataQuery)

	const { user } = useAuth()
	/* support-session indicator for HQ admins working inside a customer company */
	const showAdminBar = !!user && isAdmin(user)
	useEffect(() => {
		if (!showAdminBar) {
			return
		}
		const root = document.documentElement
		root.style.setProperty('--admin-bar-height', `${ADMIN_MODE_BAR_HEIGHT_PX}px`)
		return () => root.style.removeProperty('--admin-bar-height')
	}, [showAdminBar])

	const [, i18n] = useTranslation()
	/* a user-chosen language override always wins over the company-wide setting */
	const appLanguage = getUserLanguage() || data?.getAppConfig?.language
	if (appLanguage && appLanguage !== i18n.language) {
		i18n.changeLanguage(appLanguage)
	}

	const [activeDate, setActiveDate] = useState(new Date())
	const [selectedRegion, setSelectedRegion] = useState(null)
	const [searchInput, setSearchInput] = useState(null)

	if (error) {
		return `Error! ${error.message}`
	}

	let mainCurrency = data?.getAppConfig?.mainCurrency ? (0)
		.toLocaleString(formattingLocale, { style: 'currency', currency: data?.getAppConfig?.mainCurrency, minimumFractionDigits: 0, maximumFractionDigits: 0 })
		.replace(/\d/g, '')
		.trim() : ''

	const contextValue = {
		geoData: data?.getDashboardDSGeo,
		mainCurrency,
		branchList: data?.listShops?.items,
		regionList: data?.clientTree?.reduce((acc, dept) => acc.concat(dept.regions || []), []) || [],
		activeDate,
		dateIsToday: new Date(activeDate).toDateString() === new Date().toDateString(),
		setActiveDate,
		selectedRegion,
		setSelectedRegion,
		searchInput,
		setSearchInput
	}

	return (
		<DashboardContext.Provider value={contextValue}>
			<Wrapper className='dashboard-wrapper'>
				{showAdminBar &&
					<StyledAdminModeBarWrapper>
						<AdminModeBar
							appVersion={version}
							companyGUID={user.attributes?.['custom:company']?.toUpperCase()}
						/>
					</StyledAdminModeBarWrapper>
				}
				<AppHeader/>
				{!isLoading ?
					<Switch>
						<Route exact path={'/'} component={Dashboard}/>
						<Route exact path={'/branch/:id'} component={BranchDetails}/>
					</Switch> :
					<LoadingWrapper>
						<Spinner />
					</LoadingWrapper>
				}
			</Wrapper>
		</DashboardContext.Provider>
	)
}

export default App
